/**
 * Finanzas → **Registro de Compras 8.1** y **Registro de Ventas 14.1**.
 *
 * Portado de `Reportes::registro_compras_81` y `::registro_ventas_141` con sus vistas
 * (`menu/reportes/registro_{compras_81,ventas_141}.php`) y sus bundles.
 *
 * Es la misma pantalla para los dos registros, porque sólo cambian los datos: se elige
 * el **mes**, el estado (*activos*, *anulados* o *todos*) y —en ventas— el tipo de
 * comprobante; arriba van los totales del período y abajo la tabla con las columnas
 * que declara SUNAT (las envía la API, que es quien conoce el registro).
 *
 * Reglas heredadas:
 * - El período es **mensual** y el registro se lee del mes en curso al abrir.
 * - Por defecto se consultan los comprobantes **activos**.
 * - El archivo **PLE** de SUNAT sale con el nombre y el orden de campos del original
 *   (`LE<RUC><periodo>00080100001111.txt` y `…00140100001111.txt`), listo para
 *   cargarlo en el sistema de la SUNAT.
 *
 * Diferencias anotadas:
 * - El **PLE**, el Excel y el PDF los genera la API (el original armaba el `.txt` y el
 *   Excel en el navegador).
 * - La tabla se pinta con la definición de columnas que envía la API, así que los dos
 *   registros comparten pantalla y los importes salen alineados a la derecha.
 */

import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileSpreadsheet,
  FileText,
  Info,
  RefreshCw,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency } from '@/lib/utils';

import { registrosApi, useRegistroSunat } from '../registros-sunat.api';
import type { FiltrosRegistro, TipoRegistro } from '../registros-sunat.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** El mes en curso en `AAAA-MM`. */
function mesActual(): string {
  const hoy = new Date();

  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
}

export function RegistrosSunatPage({ registro }: { registro: TipoRegistro }) {
  const esVentas = registro === 'ventas';

  useDocumentTitle(
    esVentas ? 'Finanzas · Registro de Ventas 14.1' : 'Finanzas · Registro de Compras 8.1',
  );

  const [periodo, setPeriodo] = useState(mesActual);
  const [estado, setEstado] = useState('1');
  const [tipo, setTipo] = useState('');
  const [pagina, setPagina] = useState(1);
  // Mismo tamaño de página por defecto que el resto del panel (10, no 50).
  const [limite, setLimite] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const filtros = useMemo<FiltrosRegistro>(
    () => ({ periodo, estado, tipo: esVentas ? tipo : '', page: pagina, perPage: limite }),
    [periodo, estado, tipo, esVentas, pagina, limite],
  );

  const consulta = useRegistroSunat(registro, filtros);

  const filas = consulta.data?.filas ?? [];
  const columnas = consulta.data?.columnas ?? [];
  const totales = consulta.data?.totales;
  const total = consulta.data?.total ?? 0;
  const paginas = consulta.data?.paginas ?? 1;

  /** Cambiar cualquier filtro vuelve a la primera página. */
  const filtrar = (cambio: () => void) => {
    setPagina(1);
    cambio();
  };

  const exportar = async (formato: 'ple' | 'excel' | 'pdf') => {
    setError(null);

    const archivos = consulta.data?.archivos;
    const nombre =
      formato === 'ple'
        ? (archivos?.ple ?? `ple-${periodo.replace('-', '')}.txt`)
        : formato === 'excel'
          ? (archivos?.excel ?? `${registro}-${periodo.replace('-', '')}.csv`)
          : (archivos?.pdf ?? `${registro}-${periodo.replace('-', '')}.pdf`);

    try {
      const url =
        formato === 'ple'
          ? registrosApi.urlPle(registro, filtros)
          : formato === 'excel'
            ? registrosApi.urlExcel(registro, filtros)
            : registrosApi.urlPdf(registro, filtros);

      await abrirPdfAutenticado(url, { nombre });

      if (formato === 'ple') {
        setAviso(
          `Se descargó ${nombre}. Ese archivo es el que se carga en el sistema de la SUNAT.`,
        );
        setTimeout(() => setAviso(null), 9000);
      }
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo generar la exportación.');
    }
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">
            {esVentas ? 'Registro de Ventas 14.1' : 'Registro de Compras 8.1'}
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            {esVentas
              ? 'Las facturas, boletas y notas del mes tal como se declaran a la SUNAT, con su archivo PLE listo para cargar.'
              : 'Las compras del mes con su proveedor, base imponible e IGV, tal como se declaran a la SUNAT.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            leftIcon={<Download className="size-4" aria-hidden="true" />}
            onClick={() => void exportar('ple')}
          >
            Archivo PLE
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<FileSpreadsheet className="size-4" aria-hidden="true" />}
            onClick={() => void exportar('excel')}
          >
            Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
            onClick={() => void exportar('pdf')}
          >
            PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="No se pudo generar la exportación">
          {error}
        </Alert>
      ) : null}

      {/* Filtros */}
      <Card>
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-4">
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
            Período (mes)
            <CampoFecha
              tipo="month"
              value={periodo}
              onChange={(evento) => filtrar(() => setPeriodo(evento.target.value))}
              aria-label="Período"
            />
          </label>

          <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
            Estado
            <Select
              value={estado}
              onChange={(evento) => filtrar(() => setEstado(evento.target.value))}
              className={CONTROL}
              aria-label="Estado"
            >
              {(consulta.data?.estados ?? []).map((item) => (
                <option key={item.etiqueta} value={item.valor}>
                  {item.etiqueta}
                </option>
              ))}
            </Select>
          </label>

          {esVentas ? (
            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Tipo de comprobante
              <Select
                value={tipo}
                onChange={(evento) => filtrar(() => setTipo(evento.target.value))}
                className={CONTROL}
                aria-label="Tipo de comprobante"
              >
                {(consulta.data?.tipos ?? []).map((item) => (
                  <option key={item.etiqueta} value={item.valor}>
                    {item.etiqueta}
                  </option>
                ))}
              </Select>
            </label>
          ) : null}

          <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
            Filas
            <Select
              value={String(limite)}
              onChange={(evento) => filtrar(() => setLimite(Number(evento.target.value)))}
              className={CONTROL}
              aria-label="Filas por página"
            >
              {(consulta.data?.tamanos ?? [10, 25, 50, 100]).map((tamano) => (
                <option key={tamano} value={String(tamano)}>
                  {tamano}
                </option>
              ))}
            </Select>
          </label>
        </CardContent>
      </Card>

      {/* Totales del período */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardContent className="flex flex-col gap-0.5 p-4">
            <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
              Registros
            </p>
            <p className="text-base font-bold text-ink-900 tabular-nums sm:text-lg">
              {(totales?.registros ?? 0).toLocaleString('es-PE')}
            </p>
            <p className="text-xs text-ink-500">
              del período {consulta.data?.periodo ?? periodo}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-0.5 p-4">
            <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
              Base imponible
            </p>
            <p className="text-base font-bold break-words text-ink-900 tabular-nums sm:text-lg">
              {formatCurrency(totales?.base_imponible ?? 0)}
            </p>
            <p className="text-xs text-ink-500">
              base {formatCurrency(totales?.base_imponible ?? 0)} + IGV 18 %
            </p>
          </CardContent>
        </Card>

        <Card className="border-sky-200 bg-sky-50/50">
          <CardContent className="flex flex-col gap-0.5 p-4">
            <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">IGV</p>
            <p className="text-base font-bold break-words text-sky-800 tabular-nums sm:text-lg">
              {formatCurrency(totales?.igv ?? 0)}
            </p>
            <p className="text-xs text-ink-500">18 % de la base imponible</p>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="flex flex-col gap-0.5 p-4">
            <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
              Importe total
            </p>
            <p className="text-base font-bold break-words text-emerald-800 tabular-nums sm:text-lg">
              {formatCurrency(totales?.total ?? 0)}
            </p>
            <p className="text-xs text-ink-500">
              {totales?.otros_tributos ? `otros tributos ${formatCurrency(totales.otros_tributos)}` : 'total declarado'}
            </p>
          </CardContent>
        </Card>
      </div>

      {esVentas && totales ? (
        <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-500">
          <span>Valor exportación: {formatCurrency(totales.valor_exportacion ?? 0)}</span>
          <span>Exonerado: {formatCurrency(totales.exonerado ?? 0)}</span>
          <span>Inafecto: {formatCurrency(totales.inafecto ?? 0)}</span>
          <span>ISC: {formatCurrency(totales.isc ?? 0)}</span>
          <span>Otros tributos: {formatCurrency(totales.otros_tributos ?? 0)}</span>
        </p>
      ) : null}

      {/* Listado */}
      <Card>
        <CardContent className="space-y-3 p-4">
          {consulta.isError ? (
            <Alert variant="error" title="No se pudo leer el registro">
              {consulta.error instanceof ApiError
                ? consulta.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
            </Alert>
          ) : null}

          <div className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2 whitespace-nowrap">N°</th>
                  {columnas.map((columna) => (
                    <th
                      key={columna.clave}
                      className={cn('px-2 py-2 whitespace-nowrap', columna.derecha && 'text-right')}
                    >
                      {columna.titulo}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {consulta.isLoading
                  ? Array.from({ length: 6 }).map((_, fila) => (
                      <tr key={fila} className="border-t border-ink-100">
                        {Array.from({ length: Math.max(columnas.length, 6) + 1 }).map(
                          (__, columna) => (
                            <td key={columna} className="px-2 py-3">
                              <Skeleton className="h-4 w-full" />
                            </td>
                          ),
                        )}
                      </tr>
                    ))
                  : filas.map((fila, indice) => (
                      <tr
                        key={`${fila.cuo ?? indice}-${indice}`}
                        className={cn(
                          'border-t border-ink-100',
                          fila.estado_texto === 'ANULADO' && 'bg-rose-50/40 text-ink-500',
                        )}
                      >
                        <td className="px-2 py-2 text-right text-ink-500 tabular-nums">
                          {(pagina - 1) * limite + indice + 1}
                        </td>

                        {columnas.map((columna) => {
                          const valor = fila[columna.clave];

                          return (
                            <td
                              key={columna.clave}
                              className={cn(
                                'px-2 py-2 whitespace-nowrap',
                                columna.derecha
                                  ? 'text-right font-semibold tabular-nums'
                                  : 'max-w-[18rem] truncate',
                                columna.clave === 'total' && 'text-emerald-700',
                                fila.estado_texto === 'ANULADO' && 'line-through',
                              )}
                              title={columna.derecha ? undefined : String(valor ?? '')}
                            >
                              {columna.importe
                                ? Number(valor ?? 0).toFixed(2)
                                : (valor ?? '—')}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
              </tbody>
              {!consulta.isLoading && filas.length > 0 ? (
                <tfoot>
                  <tr className="border-t border-ink-200 bg-ink-50/60 text-[12px] font-semibold text-ink-700">
                    <td className="px-2 py-2" colSpan={columnas.length + 1}>
                      {totales
                        ? `${totales.registros.toLocaleString('es-PE')} registro(s) · base ${formatCurrency(
                            totales.base_imponible,
                          )} · IGV ${formatCurrency(totales.igv)} · total ${formatCurrency(
                            totales.total,
                          )}`
                        : ''}
                    </td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-ink-500">
              Página {pagina} de {paginas.toLocaleString('es-PE')}
              {total > 0
                ? ` · mostrando ${((pagina - 1) * limite + 1).toLocaleString('es-PE')}–${Math.min(
                    pagina * limite,
                    total,
                  ).toLocaleString('es-PE')} de ${total.toLocaleString('es-PE')}`
                : ''}
            </span>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagina <= 1}
                leftIcon={<ChevronLeft className="size-4" aria-hidden="true" />}
                onClick={() => setPagina((actual) => Math.max(1, actual - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagina >= paginas}
                rightIcon={<ChevronRight className="size-4" aria-hidden="true" />}
                onClick={() => setPagina((actual) => actual + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        {esVentas
          ? 'El registro une facturas, boletas, notas de crédito y notas de débito del mes; la base imponible y el IGV se calculan al 18 % sobre el total de cada venta, como en el registro anterior.'
          : 'La base imponible y el IGV se calculan al 18 % sobre el importe total de cada compra, como en el registro anterior; la percepción va en «otros tributos».'}
      </p>
    </div>
  );
}
